import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Move keyboard focus to an element that only exists after the next update, such as a card that remounts in a new
 * list or the control that replaces a removed one. Call `focusLater` with a finder before the update; once after
 * the next change of `version`, the finder runs and the element it returns is focused. A finder that finds
 * nothing is dropped, so it can't take focus on a later update.
 *
 * @param {*} version Value that changes when the update lands, for example the board's layout.
 * @returns {(find: () => HTMLElement|null|undefined) => void} Schedules focus for the element the finder returns.
 */
export function useFocusAfterUpdate(version) {
    const [request, setRequest] = useState(null);
    const versionRef = useRef(version);
    versionRef.current = version;

    useEffect(() => {
        if (!request || request.version === version) return undefined;
        const frame = requestAnimationFrame(() => {
            request.find()?.focus();
            setRequest(null);
        });
        return () => cancelAnimationFrame(frame);
    }, [request, version]);

    return useCallback((find) => setRequest({ find, version: versionRef.current }), []);
}
